/* eslint-disable react/require-default-props */
import React, { useMemo } from 'react'

import geojson2svg from 'geojson-to-svg'

interface TypesCordenates {
  coordinates: [string | number, string | number]
  fill: string
  fillOpacity?: number
  multi?: boolean,
  className?: string;
  /**
   * Máximo de pontos usados para desenhar o polígono. Valores menores deixam o
   * SVG mais leve (útil em listas), mas menos fiel ao formato real. Passe um
   * número maior para desenhos mais detalhados, ou 0/undefined para não reduzir.
   * @default 120
   */
  maxPontos?: number
}

/**
 * O SVG é exibido como thumbnail, então normalmente não precisamos de todos os
 * pontos do polígono. Reduzir por amostragem uniforme mantém o formato mas baixa
 * o custo de gerar o SVG e o tamanho do <path> no DOM — o que evitava o
 * travamento da UI ao renderizar vários talhões de uma vez. A quantidade é
 * controlável via prop `maxPontos`.
 */
const MAX_PONTOS_PADRAO = 120

function decimarPontos<T>(lista: T[], maxPontos: number): T[] {
  // maxPontos <= 0 desativa a redução (mantém todos os pontos)
  if (!Array.isArray(lista) || maxPontos <= 0 || lista.length <= maxPontos) return lista
  const passo = lista.length / maxPontos
  const reduzida: T[] = []
  for (let i = 0; i < maxPontos; i++) {
    reduzida.push(lista[Math.floor(i * passo)])
  }
  // garante fechamento do polígono mantendo o último ponto original
  reduzida.push(lista[lista.length - 1])
  return reduzida
}

const Svg: React.FC<TypesCordenates> = (props) => {
  const { coordinates, fill = 'green', multi = false, fillOpacity = 1, className, maxPontos = MAX_PONTOS_PADRAO } = props

  // Só recalcula quando as props relevantes mudam (geojson2svg é caro)
  const svg = useMemo(() => {
    let pontos = []
    let features = []

    if (coordinates === undefined) {
      features = [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [0, 0],
          },
          properties: {
            thumb: 'basic',
          },
        },
      ]
    }

    if (multi) {
      coordinates?.forEach((talhao) => {
        // @ts-ignore
        const validos = talhao.filter((e) => (e.latitude || e.lat) && (e.longitude || e.lng))
        // decima antes de mapear: thumbnail não precisa de todos os pontos
        decimarPontos(validos, maxPontos).forEach((dados: any) => {
          // push em vez de recriar o array (o spread anterior era O(n²) e travava com muitos pontos)
          pontos.push([
            dados.longitude || String(dados.lng),
            dados.longitude || dados.lng,
          ])
        })
      })

      features = [
        ...features,
        {
          type: 'Feature',
          geometry: {
            type: 'MultiPolygon',
            coordinates: [pontos],
          },
          properties: {
            thumb: 'basic',
          },
        },
      ]
    } else {
      // @ts-ignore
      const validos = coordinates?.filter((e: any) => (e.latitude || e.lat) && (e.longitude || e.lng)) ?? []
      decimarPontos(validos, maxPontos).forEach((dados: any) => {
        pontos.push([
          dados.longitude || String(dados.lng),
          -(dados.latitude || dados.lat),
        ])
      })
      // mantinha a ordem invertida do código original (era construído de trás pra frente)
      pontos.reverse()

      features = [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [pontos],
          },
          properties: {
            thumb: 'basic',
          },
        },
      ]
    }

    if (pontos.length === 0) return null

    return geojson2svg()
      .type('thumb')
      .styles({
        basic: { fill, fillOpacity, strokeColor: '#ffffff' },
      })
      .data({
        type: 'FeatureCollection',
        features,
      })
      .render()
  }, [coordinates, fill, fillOpacity, multi, maxPontos])

  return (
    <svg
      style={{ maxWidth: '50px', maxHeight: '50px', marginRight: 0 }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svg ?? '' }}
      className={className ?? ""}
    />
  )
}

/**
 * Memoizado: geojson2svg() é caro e roda por render. Evita reprocessar quando
 * as props não mudam (ex.: re-renders do formulário durante digitação).
 */
export default React.memo(Svg)
